import { useEffect, useState, useCallback } from "react";
import { api } from "@/lib/api";
import { useAuth } from "@/lib/auth-context";
import { toast } from "sonner";
import { Button } from "@/components/ui/button";
import { DarkPage } from "@/components/app/PageHeader";
import { ShieldCheck, ShieldAlert, Copy, Trash2 } from "lucide-react";

const EMPTY_FORM = { display_name: "", issuer_url: "", client_id: "", client_secret: "", default_role: "member", email_domain: "" };

export default function SSOSettings() {
  const { user } = useAuth();
  const isManager = user?.role === "admin" || user?.role === "owner";

  const [connection, setConnection] = useState(undefined); // undefined = loading, null = not configured
  const [form, setForm] = useState(EMPTY_FORM);
  const [saving, setSaving] = useState(false);

  const load = useCallback(async () => {
    try {
      const { data } = await api.get("/sso/connections");
      setConnection(data || null);
    } catch {
      toast.error("Failed to load SSO settings");
      setConnection(null);
    }
  }, []);

  useEffect(() => { load(); }, [load]);

  const create = async () => {
    if (!form.display_name.trim() || !form.issuer_url.trim() || !form.client_id.trim() || !form.client_secret.trim()) {
      return toast.error("Display name, issuer URL, client ID and client secret are required");
    }
    setSaving(true);
    try {
      await api.post("/sso/connections", { ...form, email_domain: form.email_domain || null });
      toast.success("SSO configured");
      setForm(EMPTY_FORM);
      load();
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Failed to configure SSO — check the issuer URL is a valid OIDC provider");
    } finally {
      setSaving(false);
    }
  };

  const toggleEnabled = async () => {
    try {
      await api.put(`/sso/connections/${connection.connection_id}`, { is_enabled: !connection.is_enabled });
      toast.success(connection.is_enabled ? "SSO disabled" : "SSO enabled");
      load();
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Failed to update");
    }
  };

  const remove = async () => {
    if (!window.confirm("Remove SSO configuration? Employees will need to sign in with email/password or Google instead.")) return;
    try {
      await api.delete(`/sso/connections/${connection.connection_id}`);
      toast.success("SSO configuration removed");
      load();
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Failed to remove");
    }
  };

  const copyLoginUrl = () => {
    navigator.clipboard.writeText(connection.login_url);
    toast.success("Login link copied");
  };

  if (!isManager) {
    return (
      <div className="p-10 text-center text-sm text-[#7d8590] flex flex-col items-center gap-2">
        <ShieldAlert className="w-6 h-6" />
        Only workspace owners and admins can manage SSO.
      </div>
    );
  }

  return (
    <DarkPage><div className="p-6 max-w-2xl mx-auto">
      <div className="mb-6">
        <h1 className="text-2xl font-bold text-white flex items-center gap-2"><ShieldCheck className="w-5 h-5 text-[#7d8590]" /> Single Sign-On</h1>
        <p className="text-sm text-[#7d8590] mt-1">Let your team sign in with your company's identity provider (Okta, Azure AD, Google Workspace, or any OIDC-compliant provider).</p>
      </div>

      {connection === undefined ? (
        <div className="text-sm text-[#7d8590]">Loading…</div>
      ) : connection ? (
        <div className="bg-[#0d1117] border border-[#21262d] rounded-lg p-5 space-y-4">
          <div className="flex items-center justify-between">
            <div>
              <div className="text-white font-semibold">{connection.display_name}</div>
              <div className="text-xs text-[#7d8590] mt-0.5 font-mono">{connection.issuer_url}</div>
            </div>
            <span className={`text-xs px-2 py-0.5 rounded-full border ${connection.is_enabled ? "text-[#00F5D4] bg-[#00F5D4]/10 border-[#00F5D4]/20" : "text-[#7d8590] bg-[#1c2128] border-[#30363d]"}`}>
              {connection.is_enabled ? "enabled" : "disabled"}
            </span>
          </div>

          <div className="grid grid-cols-2 gap-4 text-sm">
            <div>
              <div className="text-xs text-[#7d8590] mb-0.5">New members join as</div>
              <div className="text-white capitalize">{connection.default_role}</div>
            </div>
            <div>
              <div className="text-xs text-[#7d8590] mb-0.5">Email domain</div>
              <div className="text-white">{connection.email_domain || "not set — share the login link directly"}</div>
            </div>
          </div>

          <div>
            <div className="text-xs text-[#7d8590] mb-1">Employee login link</div>
            <div className="flex items-center gap-2">
              <code className="flex-1 bg-[#161b22] border border-[#30363d] rounded-md px-3 py-2 text-xs text-[#9CA3AF] truncate">{connection.login_url}</code>
              <button onClick={copyLoginUrl} className="text-[#7d8590] hover:text-white"><Copy className="w-4 h-4" /></button>
            </div>
          </div>

          <div className="flex gap-2 pt-2 border-t border-[#21262d]">
            <Button onClick={toggleEnabled} variant="outline" className="border-[#30363d] text-[#7d8590] hover:text-white">
              {connection.is_enabled ? "Disable" : "Enable"}
            </Button>
            <Button onClick={remove} variant="outline" className="border-rose-500/20 text-rose-400 hover:bg-rose-500/10">
              <Trash2 className="w-4 h-4 mr-1" /> Remove
            </Button>
          </div>
        </div>
      ) : (
        <div className="bg-[#0d1117] border border-[#21262d] rounded-lg p-5 space-y-4">
          <div className="grid grid-cols-2 gap-3">
            <div>
              <label className="text-xs text-[#7d8590] mb-1 block">Provider name *</label>
              <input value={form.display_name} onChange={e => setForm(f => ({ ...f, display_name: e.target.value }))}
                placeholder="e.g. Okta"
                className="w-full bg-[#161b22] border border-[#30363d] text-white rounded-md px-3 py-2 text-sm" />
            </div>
            <div>
              <label className="text-xs text-[#7d8590] mb-1 block">New members join as</label>
              <select value={form.default_role} onChange={e => setForm(f => ({ ...f, default_role: e.target.value }))}
                className="w-full bg-[#161b22] border border-[#30363d] text-white rounded-md px-3 py-2 text-sm">
                <option value="member">Member</option>
                <option value="admin">Admin</option>
              </select>
            </div>
          </div>
          <div>
            <label className="text-xs text-[#7d8590] mb-1 block">Issuer URL *</label>
            <input value={form.issuer_url} onChange={e => setForm(f => ({ ...f, issuer_url: e.target.value }))}
              placeholder="https://your-org.okta.com"
              className="w-full bg-[#161b22] border border-[#30363d] text-white rounded-md px-3 py-2 text-sm font-mono" />
            <p className="text-[11px] text-[#7d8590] mt-1">We'll fetch <code>{"{issuer}/.well-known/openid-configuration"}</code> to discover the rest automatically.</p>
          </div>
          <div className="grid grid-cols-2 gap-3">
            <div>
              <label className="text-xs text-[#7d8590] mb-1 block">Client ID *</label>
              <input value={form.client_id} onChange={e => setForm(f => ({ ...f, client_id: e.target.value }))}
                className="w-full bg-[#161b22] border border-[#30363d] text-white rounded-md px-3 py-2 text-sm font-mono" />
            </div>
            <div>
              <label className="text-xs text-[#7d8590] mb-1 block">Client secret *</label>
              <input type="password" value={form.client_secret} onChange={e => setForm(f => ({ ...f, client_secret: e.target.value }))}
                className="w-full bg-[#161b22] border border-[#30363d] text-white rounded-md px-3 py-2 text-sm font-mono" />
            </div>
          </div>
          <div>
            <label className="text-xs text-[#7d8590] mb-1 block">Email domain (optional)</label>
            <input value={form.email_domain} onChange={e => setForm(f => ({ ...f, email_domain: e.target.value }))}
              placeholder="acme.com"
              className="w-full bg-[#161b22] border border-[#30363d] text-white rounded-md px-3 py-2 text-sm" />
            <p className="text-[11px] text-[#7d8590] mt-1">If set, anyone typing an @acme.com email on the login page is auto-routed to SSO. Otherwise, share the login link directly.</p>
          </div>
          <Button onClick={create} disabled={saving} className="bg-[#00F5D4] text-[#030712] hover:bg-[#00F5D4]/90 font-semibold">
            {saving ? "Configuring…" : "Configure SSO"}
          </Button>
        </div>
      )}
    </div></DarkPage>
  );
}
